import React, { useState, useMemo } from 'react';
import styled from '@emotion/styled';
import toLower from 'lodash/toLower';
import isEmpty from 'lodash/isEmpty';
import Textfield from '@atlaskit/textfield';
import Popup from '@atlaskit/popup';
import Badge from '@atlaskit/badge';
import SearchIcon from '@atlaskit/icon/glyph/search';
import CrossCircleIcon from '@atlaskit/icon/glyph/cross-circle';
import Button, { LoadingButton } from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import SectionMessage from '@atlaskit/section-message';
import Form, { FormFooter } from '@atlaskit/form';
import isEmail from 'validator/lib/isEmail';
import { SearchField } from './search-field';
import { AddCustomerEmailField } from '../../../../entitlement-transfer/src/components/add-customer-email-field';
import { getSupportUrl } from '@css-xp/ppc-common';

const StyledSearchAndFilterContainer = styled.div({
  marginBottom: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
});

const StyledFilterRow = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
});

const StyledFilterContent = styled.div({
  padding: '8px',
  display: 'flex',
  flexDirection: 'column',
  width: '23vw',
});

const StyledFilterLineItems = styled.div({
  marginTop: '12px',
  padding: '0 20px 20px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  maxHeight: '350px',
  overflow: 'auto',
});

const StyledClearFilterButton = styled(Button)({
  paddingLeft: 0,
  paddingRight: 0,
  marginBottom: '10px',
});

interface User {
  email: string;
  error?: string | undefined;
}

type EmailList = Map<string, User>;

export const SearchAndFilterContainer = ({
  searchQuery,
  selectedLineItems,
  emailList,
  setEmailList,
  onQueryChange,
  onSelectedLineItemsChange,
  onTransferSearchFormSubmit,
  clearSearchQuery,
  isSearchLineItemsLoading = false,
  showTransferForm = false,
  showTransferSearchResults = false,
  showFreeTextContent = false,
  filterAddon,
  searchLineItems,
  searchFieldPlaceholder = 'Search by Entitlement number or cloudsite',
  filterSearchFieldPlaceholder = 'Select at least one billing profile',
  filterTriggerLabel = 'Filter by billing profile',
  isLoading = false,
}: {
  searchQuery?: string;
  selectedLineItems?: string[];
  filterSearchFieldPlaceholder?: string;
  searchFieldPlaceholder?: string;
  filterTriggerLabel?: string;
  onQueryChange: (_searchQuery: string) => void;
  onSelectedLineItemsChange: (_selectedLineItems: string[]) => void;
  clearSearchQuery: () => void;
  searchLineItems: {
    label: string;
    value: string;
  }[];
  emailList: Map<string, User>;
  setEmailList?: React.Dispatch<React.SetStateAction<EmailList>>;
  isSearchLineItemsLoading?: boolean;
  showTransferForm?: boolean;
  showTransferSearchResults?: boolean;
  showFreeTextContent?: boolean;
  filterAddon?: JSX.Element;
  onTransferSearchFormSubmit: (data: any) => Promise<void>;
  isLoading?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filterSearchQuery, setFilterSearchQuery] = useState('');

  const filterLineItemListContent = useMemo(() => {
    const filteredLineItemList = !filterSearchQuery
      ? searchLineItems
      : searchLineItems.filter(({ label }) => toLower(label).includes(toLower(filterSearchQuery)));

    return filteredLineItemList.map(({ label, value }) => {
      const isChecked = selectedLineItems?.includes(value) === true;
      const onChange = () => {
        let updatedSelectedLineItems;

        if (isChecked) {
          updatedSelectedLineItems = selectedLineItems.filter((selectedLineItem) => selectedLineItem !== value);
        } else {
          updatedSelectedLineItems = [...(selectedLineItems || []), value];
        }

        onSelectedLineItemsChange(updatedSelectedLineItems);
      };

      return (
        <Checkbox
          key={value}
          value={value}
          label={label}
          onChange={onChange}
          name="checkbox-default"
          isChecked={isChecked}
        />
      );
    });
  }, [filterSearchQuery, searchLineItems, selectedLineItems, onSelectedLineItemsChange]);

  const popupContent = (
    <div>
      {!!selectedLineItems?.length && (
        <StyledClearFilterButton onClick={() => onSelectedLineItemsChange([])} appearance="link">
          Clear selected items
        </StyledClearFilterButton>
      )}
      <Textfield
        elemAfterInput={
          isEmpty(filterSearchQuery) ? (
            <SearchIcon label="search" />
          ) : (
            <Button
              iconAfter={<CrossCircleIcon label="clear" />}
              onClick={() => setFilterSearchQuery('')}
              appearance="subtle"
              testId="clear-filter-search-query"
            />
          )
        }
        value={filterSearchQuery}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterSearchQuery(e.target.value)}
        placeholder={filterSearchFieldPlaceholder}
      />
      <StyledFilterLineItems>{filterLineItemListContent}</StyledFilterLineItems>
    </div>
  );

  const isTransferSearchSubmitBtnDisabled = useMemo(() => {
    const emailListArray = Array.from(emailList);
    if (!emailListArray.length) return true;

    const invalidEmail = emailListArray.find(([_key, { email }]) => {
      return !email || !isEmail(email);
    });

    return !!invalidEmail;
  }, [emailList]);

  return (
    <Form onSubmit={onTransferSearchFormSubmit}>
      {({ formProps }) => (
        <form
          {...formProps}
          onKeyPress={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
            }
          }}
        >
          <StyledSearchAndFilterContainer>
            <SearchField
              searchQuery={searchQuery}
              onQueryChange={onQueryChange}
              clearSearchQuery={clearSearchQuery}
              isDisabled={isSearchLineItemsLoading || showTransferSearchResults}
              placeholder={searchFieldPlaceholder}
            />

            {!showTransferForm && (
              <StyledFilterRow>
                <Popup
                  isOpen={isOpen}
                  onClose={() => setIsOpen(false)}
                  placement="bottom-start"
                  content={() => <StyledFilterContent>{popupContent}</StyledFilterContent>}
                  trigger={(triggerProps) => (
                    <Button
                      {...triggerProps}
                      appearance="default"
                      isSelected={isOpen}
                      onClick={() => setIsOpen(!isOpen)}
                      isDisabled={isSearchLineItemsLoading || isEmpty(searchLineItems) || showFreeTextContent}
                    >
                      {filterTriggerLabel}{' '}
                      {!!selectedLineItems?.length && <Badge appearance="primary">{selectedLineItems.length}</Badge>}
                    </Button>
                  )}
                />

                {filterAddon}
              </StyledFilterRow>
            )}

            {showFreeTextContent && (
              <SectionMessage appearance="warning">
                If an external billing account manages this, transfer it to your account by entering the customer’s
                email address. In case this belongs to a different customer in the same billing account, contact the{' '}
                {getSupportUrl()} to move it to another customer. Contact the {getSupportUrl()} for any annual legacy
                billing system support. For any Atlassian Guard transfers use the E-# and an authorized customer billing
                admin email.
              </SectionMessage>
            )}

            {showTransferForm && !showTransferSearchResults && (
              <>
                <SectionMessage appearance="warning">
                  If an external billing account manages this, transfer it to your account by entering the customer's
                  email address. In case this belongs to a different customer in the same billing account, contact the{' '}
                  {getSupportUrl()} to move it to another customer. Contact the {getSupportUrl()} for any annual and
                  legacy billing system support.
                </SectionMessage>
                <AddCustomerEmailField emailList={emailList} setEmailList={setEmailList} />

                <LoadingButton
                  appearance="primary"
                  type="submit"
                  isLoading={isLoading}
                  testId="submit-transfer-form"
                  isDisabled={isTransferSearchSubmitBtnDisabled}
                >
                  Next
                </LoadingButton>
              </>
            )}
          </StyledSearchAndFilterContainer>
        </form>
      )}
    </Form>
  );
};
